import { GenerateABDFDTData } from "@/api/generate-abd-fdt";
import { OPMEvidenceData } from "@/api/opm-evidence";
import { OPMFATData } from "@/api/opm-fat";
import { OPMFDTData } from "@/api/opm-fdt";
import { OPMPoleData } from "@/api/opm-pole";

// Gabungkan semua kemungkinan sumber data ke dalam satu tipe untuk kemudahan
export interface AllDataSources {
  opmFdts?: OPMFDTData[];
  opmFats?: OPMFATData[];
  abdFdts?: GenerateABDFDTData[];
  abdFatCount?: number;
  opmEvidences?: OPMEvidenceData[];
  opmPoles?: OPMPoleData[];
  abdPoleCount?: number;
}

// Peta ini akan mengambil data dari sumber yang relevan
// dan memberikannya ke fungsi validasi.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const dataMappers: Record<string, (sources: AllDataSources) => any> = {
  A01: (sources) => sources.opmFdts,
  A02: (sources) => sources.opmFdts,
  A03: (sources) => sources.opmFdts,
  A04: (sources) => sources.opmFdts,
  A05: (sources) => ({
    opmFats: sources.opmFats,
    abdFatCount: sources.abdFatCount,
  }),
  A06: (sources) => ({
    opmFats: sources.opmFats,
    abdFatCount: sources.abdFatCount,
  }),
  A07: (sources) => ({
    opmFats: sources.opmFats,
    abdFatCount: sources.abdFatCount,
  }),
  A08: (sources) => ({
    opmFats: sources.opmFats,
    abdFatCount: sources.abdFatCount,
    opmFdts: sources.opmFdts,
    opmPoles: sources.opmPoles,
    abdPoleCount: sources.abdPoleCount,
  }),
  A09: (sources) => ({
    opmFats: sources.opmFats,
    abdFatCount: sources.abdFatCount,
    opmFdts: sources.opmFdts,
  }),
  A10: (sources) => sources.opmFdts,
  A11: (sources) => ({
    opmFats: sources.opmFats,
    abdFatCount: sources.abdFatCount,
  }),
  A12: (sources) => ({
    opmFats: sources.opmFats,
    abdFatCount: sources.abdFatCount,
  }),
  A13: (sources) => sources.opmEvidences,
  A14: (sources) => sources.opmEvidences,
  A15: (sources) => sources.opmEvidences,
  A16: (sources) => sources.opmEvidences,
};

// Fallback jika mapper tidak ditemukan
export const defaultMapper = () => null;

// Override evidence yang baru saja berhasil disubmit jadi valid, tanpa
// menunggu refetch — dipakai saat hitung ulang status tepat setelah submit
// sukses (lihat getStatusForSubmit di ValidationProvider).
export function overrideEvidenceValid(
  opmEvidences: OPMEvidenceData[] | undefined,
  type: OPMEvidenceData["opmEvidenceType"],
): OPMEvidenceData[] {
  const others = (opmEvidences ?? []).filter(
    (evidence) => evidence.opmEvidenceType !== type,
  );
  return [
    ...others,
    { opmEvidenceType: type, isValid: true } as OPMEvidenceData,
  ];
}

// Sama seperti overrideEvidenceValid, tapi untuk pole. Harus upsert per poleId,
// bukan append: getPoleEntityStatus membandingkan jumlah pole dengan
// abdPoleCount, jadi menambah baris akan menggandakan hitungan.
export function overridePoleValid(
  opmPoles: OPMPoleData[] | undefined,
  poleId: string,
): OPMPoleData[] {
  const list = opmPoles ?? [];
  const existing = list.find((pole) => pole.poleId === poleId);

  return [
    ...list.filter((pole) => pole.poleId !== poleId),
    {
      ...(existing ?? {}),
      poleId,
      isValid: true,
      poleIdStatus: true,
      // getLatestPolesByPoleId memilih record terbaru per poleId, jadi entri
      // ini harus lebih baru dari record lama yang mungkin masih ada.
      updatedDate: new Date().toISOString(),
    } as OPMPoleData,
  ];
}

// Upsert per fatId. Satu submit FAT mengirim seluruh media & teks entitas
// sekaligus, jadi sukses berarti semua status field FAT itu diterima.
export function overrideFatValid(
  opmFats: OPMFATData[] | undefined,
  fatId: string,
): OPMFATData[] {
  const list = opmFats ?? [];
  const existing = list.find((fat) => fat.fatId === fatId);

  return [
    ...list.filter((fat) => fat.fatId !== fatId),
    {
      ...(existing ?? {}),
      fatId,
      valid: true,
      poleFatStatus: true,
      imageFatStatus: true,
      outputFatStatus: true,
      splitterFatStatus: true,
    } as OPMFATData,
  ];
}

// FDT di-key lewat indeks, bukan id: OPMFDTData tidak punya field fdtId dan
// FDTAccordionItems memasangkan abdFdts[index] dengan opmFdts[index].
export function overrideFdtValid(
  opmFdts: OPMFDTData[] | undefined,
  index: number,
): OPMFDTData[] {
  const list = [...(opmFdts ?? [])];

  list[index] = {
    ...(list[index] ?? {}),
    valid: true,
    revise: false,
    poleFdtStatus: true,
    imageFdtStatus: true,
    outputFdtStatus: true,
    inputFdtStatus: true,
    splitterFdtStatus: true,
  } as OPMFDTData;

  return list;
}

// ---------------------------------------------------------------------------
// Kebalikan dari override*Valid, dipakai saat submit ditolak backend (400).
// Jalur sukses tidak menunggu refetch — ia menambal data secara lokal lalu
// hitung ulang. Jalur error memakai cermin yang sama supaya sinkron dan tidak
// bergantung pada urutan datangnya refetch.
// ---------------------------------------------------------------------------

// Peta key error dari body 400 ke field status entitas. Key teks hasil OCR
// dipetakan ke foto sumbernya: fatIdText dibaca dari foto FAT, outputFatText
// dari foto output.
export const FAT_ERROR_FIELD_TO_STATUS: Record<string, keyof OPMFATData> = {
  poleFatMedia: "poleFatStatus",
  imageFatMedia: "imageFatStatus",
  fatIdText: "imageFatStatus",
  outputFatMedia: "outputFatStatus",
  outputFatText: "outputFatStatus",
  splitterFatMedia: "splitterFatStatus",
};

export const FDT_ERROR_FIELD_TO_STATUS: Record<string, keyof OPMFDTData> = {
  poleFdtMedia: "poleFdtStatus",
  imageFdtMedia: "imageFdtStatus",
  fdtIdText: "imageFdtStatus",
  inputFdtMedia: "inputFdtStatus",
  inputFdtText: "inputFdtStatus",
  outputFdtMedia: "outputFdtStatus",
  outputFdtText: "outputFdtStatus",
  splitterFdtMedia: "splitterFdtStatus",
};

const FAT_STATUS_FIELDS: (keyof OPMFATData)[] = [
  "poleFatStatus",
  "imageFatStatus",
  "outputFatStatus",
  "splitterFatStatus",
];

const FDT_STATUS_FIELDS: (keyof OPMFDTData)[] = [
  "poleFdtStatus",
  "imageFdtStatus",
  "inputFdtStatus",
  "outputFdtStatus",
  "splitterFdtStatus",
];

// Ambil field status yang gagal dari body 400, yang berbentuk
// [{ imageFatMedia: "...", fatIdText: "..." }, ...].
export function statusFieldsFromErrors<T>(
  errors: unknown,
  fieldMap: Record<string, keyof T>,
): (keyof T)[] {
  const list = Array.isArray(errors) ? errors : [];
  const fields = new Set<keyof T>();

  for (const item of list) {
    if (!item || typeof item !== "object") {
      continue;
    }
    for (const key of Object.keys(item)) {
      const field = fieldMap[key];
      if (field) {
        fields.add(field);
      }
    }
  }

  return Array.from(fields);
}

function falseFor<T>(fields: (keyof T)[]) {
  return Object.fromEntries(fields.map((field) => [field, false]));
}

// Pole cuma punya satu media, jadi tidak perlu peta field. getPoleEntityStatus
// membaca isValid, bukan poleIdStatus, jadi dua-duanya diset.
export function overridePoleInvalid(
  opmPoles: OPMPoleData[] | undefined,
  poleId: string,
): OPMPoleData[] {
  const list = opmPoles ?? [];
  const existing = list.find((pole) => pole.poleId === poleId);

  return [
    ...list.filter((pole) => pole.poleId !== poleId),
    {
      ...(existing ?? {}),
      poleId,
      isValid: false,
      poleIdStatus: false,
      // getLatestPolesByPoleId memilih record terbaru per poleId.
      updatedDate: new Date().toISOString(),
    } as OPMPoleData,
  ];
}

// Hanya field yang dikeluhkan backend yang diset false. Kalau tidak ada satu
// pun key yang dikenali peta — backend menambah/mengganti nama key — jatuh ke
// seluruh field, supaya submit yang ditolak tidak pernah lolos jadi "Validated".
export function overrideFatInvalid(
  opmFats: OPMFATData[] | undefined,
  fatId: string,
  failedStatusFields: (keyof OPMFATData)[],
): OPMFATData[] {
  const list = opmFats ?? [];
  const existing = list.find((fat) => fat.fatId === fatId);
  const fields =
    failedStatusFields.length > 0 ? failedStatusFields : FAT_STATUS_FIELDS;

  return [
    ...list.filter((fat) => fat.fatId !== fatId),
    {
      ...(existing ?? {}),
      fatId,
      valid: false,
      ...falseFor(fields),
    } as OPMFATData,
  ];
}

// FDT di-key lewat indeks, sama seperti overrideFdtValid.
export function overrideFdtInvalid(
  opmFdts: OPMFDTData[] | undefined,
  index: number,
  failedStatusFields: (keyof OPMFDTData)[],
): OPMFDTData[] {
  const list = [...(opmFdts ?? [])];
  const fields =
    failedStatusFields.length > 0 ? failedStatusFields : FDT_STATUS_FIELDS;

  list[index] = {
    ...(list[index] ?? {}),
    valid: false,
    ...falseFor(fields),
  } as OPMFDTData;

  return list;
}

// Evidence tidak punya sub-field: key error-nya nama file di dalam zip.
export function overrideEvidenceInvalid(
  opmEvidences: OPMEvidenceData[] | undefined,
  type: OPMEvidenceData["opmEvidenceType"],
): OPMEvidenceData[] {
  const others = (opmEvidences ?? []).filter(
    (evidence) => evidence.opmEvidenceType !== type,
  );
  return [
    ...others,
    { opmEvidenceType: type, isValid: false } as OPMEvidenceData,
  ];
}
